import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  ClipboardPen,
  Droplets,
  FileText,
  FlaskConical,
  HeartPulse,
  LayoutDashboard,
  LayoutPanelLeft,
  NotebookPen,
  PersonStanding,
  Pill,
  Plus,
  Scissors,
  Stethoscope,
  Wind,
  type LucideIcon,
} from 'lucide-react';
import type { ResolvedEmrFormsResponse } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Alert, Button, PageSkeleton } from '@/components/ui/primitives';
import { VitalsDialog } from '@/components/VitalsDialog';
import { ClinicalShell } from '@/components/clinical-shell/ClinicalShell';
import { ClinicalNav, type ClinicalNavGroup, type ClinicalNavItem } from '@/components/clinical-shell/ClinicalNav';
import { withSection } from '@/components/clinical-shell/section-param';
import { useStickyShell } from '@/components/clinical-shell/use-sticky-shell';
import { AdmissionPatientBar } from '../admission/AdmissionPatientBar';
import { newbornFactsOf } from '../../opd/emr/completion';
import { admissionKey, useAdmission, useAdmissionLive, useCareTeam } from '../admission/queries';
import { admissionIsOpen, admissionPath, ipdEmrPath, type Admission } from '../admission/types';
import {
  IPD_EMR_HOME,
  ONE_KIND_HOSTS,
  defaultIpdForms,
  formsForPatient,
  ipdClinicalFormsVisible,
  ipdEmrChartSections,
  ipdEmrFormEntries,
  ipdEmrHosts,
  ipdEmrSectionFromParams,
  ipdEmrSectionIds,
  ipdNewOrderPath,
  isIpdClinicalForm,
  mayRecordAdmissionForm,
  type IpdEmrChartSection,
  type IpdEmrFormEntry,
  type IpdEmrFormHost,
  type IpdEmrOneKindHost,
  type IpdEmrSectionId,
} from './ipd-emr-sections';
import { ClinicalFormSection } from '../../opd/emr/ClinicalFormSection';
import { IpdEmrOverview } from './sections/IpdEmrOverview';
import { EmarSection } from './sections/EmarSection';
import { TaskChartSection } from './sections/TaskChartSection';
import { NursingNotesSection } from './sections/NursingNotesSection';
import { ProgressNotesSection } from './sections/ProgressNotesSection';
import { VitalsChartSection } from './sections/VitalsChartSection';
import { InvestigationsSection } from './sections/InvestigationsSection';
import { OrdersSection } from './sections/OrdersSection';
import { KindOrdersSection } from './sections/KindOrdersSection';
import { ProceduresSection } from './sections/ProceduresSection';
import { DischargeSummarySection } from './sections/DischargeSummarySection';

type FixedSectionId = IpdEmrChartSection | IpdEmrFormHost;

const SECTION_ICONS: Record<FixedSectionId, LucideIcon> = {
  overview: LayoutDashboard,
  vitals: HeartPulse,
  emar: Pill,
  'task-chart': CalendarClock,
  'nursing-notes': ClipboardPen,
  'progress-notes': NotebookPen,
  orders: ClipboardList,
  investigations: FlaskConical,
  procedures: Scissors,
  oxygen: Wind,
  transfusion: Droplets,
  physiotherapy: PersonStanding,
  consults: Stethoscope,
  monitoring: Activity,
  'discharge-summary': FileText,
};

const SECTION_LABELS: Record<FixedSectionId, string> = {
  overview: 'ws_emr_overview',
  vitals: 'vitals',
  emar: 'ws_emr_emar',
  'task-chart': 'io_ord_task_chart',
  'nursing-notes': 'ws_emr_nursing_notes',
  'progress-notes': 'ws_emr_progress_notes',
  orders: 'ws_emr_orders',
  investigations: 'investigations',
  procedures: 'io_ord_section_procedures',
  oxygen: 'io_ord_section_oxygen',
  transfusion: 'io_ord_section_transfusion',
  physiotherapy: 'io_ord_section_physiotherapy',
  consults: 'io_ord_section_consults',
  monitoring: 'io_ord_section_monitoring',
  'discharge-summary': 'discharge_summary',
};

function sectionIcon(id: IpdEmrSectionId): LucideIcon {
  return isIpdClinicalForm(id) ? ClipboardCheck : SECTION_ICONS[id];
}

function sectionLabel(id: IpdEmrSectionId): string {
  return isIpdClinicalForm(id) ? tr('opd', `emr_form_${id}`) : tr('ipd', SECTION_LABELS[id]);
}

export function useIpdEmrForms(admissionId: string) {
  return useQuery({
    queryKey: ['ipd-emr-forms', admissionId],
    queryFn: () => get<ResolvedEmrFormsResponse>(`/ipd/admissions/${admissionId}/emr-forms`),
    enabled: Boolean(admissionId),
    staleTime: 300_000,
  });
}

export function IpdEmrPage() {
  const { admissionId = '' } = useParams<{ admissionId: string }>();
  const admission = useAdmission(admissionId);
  const team = useCareTeam(admissionId);
  const forms = useIpdEmrForms(admissionId);
  useAdmissionLive(admissionId);

  if (admission.isLoading) return <PageSkeleton />;
  if (!admission.data) {
    return <Alert variant="destructive">{describeError(admission.error) || tr('ipd', 'that_admission_could_not_be_opened')}</Alert>;
  }
  return <IpdEmr admission={admission.data} team={team.data} forms={forms.data?.forms ?? defaultIpdForms()} />;
}

function IpdEmr({ admission, team, forms }: { admission: Admission; team: ReturnType<typeof useCareTeam>['data']; forms: Parameters<typeof ipdEmrFormEntries>[0] }) {
  const { can, hasModule, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const shell = useStickyShell();
  const [recordingVitals, setRecordingVitals] = useState(false);
  const current = admissionIsOpen(admission);
  const careTeam = [admission.doctor.id, ...(team?.members ?? []).map((member) => member.userId)];

  const chart = ipdEmrChartSections({ can });
  const entries = ipdEmrFormEntries(formsForPatient(forms, admission.patient), ipdEmrHosts({ can, hasModule }), { clinicalForms: ipdClinicalFormsVisible({ can }) });
  const available = ipdEmrSectionIds(chart, entries);
  const active = ipdEmrSectionFromParams(params, available);
  const here = (section: IpdEmrSectionId) => ipdEmrPath(admission.id, section);
  const chooserFrom = (section: IpdEmrSectionId) => ipdNewOrderPath(admission.id, here(section));
  const formKeysOf = (section: IpdEmrSectionId) => entries.find((entry) => entry.id === section)?.formKeys ?? [];

  const open = (id: string) => {
    setParams((current) => withSection(current, id, IPD_EMR_HOME), { replace: true });
    shell.scrollToTop();
  };

  const oneKind = (section: IpdEmrOneKindHost) => () => (
    <KindOrdersSection
      admission={admission}
      kind={ONE_KIND_HOSTS[section]}
      icon={SECTION_ICONS[section]}
      title={tr('ipd', SECTION_LABELS[section])}
      description={tr('ipd', `io_ord_desc_${section}`)}
      newOrderHref={ipdNewOrderPath(admission.id, here(section), ONE_KIND_HOSTS[section])}
    />
  );

  const body: Record<FixedSectionId, () => ReactNode> = {
    overview: () => <IpdEmrOverview admission={admission} team={team} available={available} onOpen={open} />,
    vitals: () => <VitalsChartSection admission={admission} onRecord={() => setRecordingVitals(true)} />,
    emar: () => <EmarSection admission={admission} newOrderHref={chooserFrom('emar')} />,
    'task-chart': () => <TaskChartSection admission={admission} />,
    'nursing-notes': () => <NursingNotesSection admission={admission} />,
    'progress-notes': () => <ProgressNotesSection admission={admission} />,
    orders: () => <OrdersSection admission={admission} icon={SECTION_ICONS.orders} newOrderHref={chooserFrom('orders')} />,
    investigations: () => <InvestigationsSection admission={admission} icon={SECTION_ICONS.investigations} formKeys={formKeysOf('investigations')} />,
    procedures: () => <ProceduresSection admission={admission} icon={SECTION_ICONS.procedures} />,
    oxygen: oneKind('oxygen'),
    transfusion: oneKind('transfusion'),
    physiotherapy: oneKind('physiotherapy'),
    consults: oneKind('consults'),
    monitoring: oneKind('monitoring'),
    'discharge-summary': () => <DischargeSummarySection admission={admission} />,
  };

  const groups = emrNavGroups(admission, chart, entries);

  return (
    <>
      <ClinicalShell
        shell={shell}
        activeKey={active}
        bar={
          <AdmissionPatientBar
            ref={shell.attachBar}
            admission={admission}
            team={team}
            actions={
              <>
                <Link to={admissionPath(admission.id)}>
                  <Button variant="outline">
                    <LayoutPanelLeft className="h-4 w-4" aria-hidden /> {tr('ipd', 'ws_ward_workspace')}
                  </Button>
                </Link>
                {current && can('vitals.write') && (
                  <Button variant="outline" onClick={() => setRecordingVitals(true)}>
                    <HeartPulse className="h-4 w-4" aria-hidden /> {tr('ipd', 'record_vitals')}
                  </Button>
                )}
                {current && can('cpoe.write') && (
                  <Link to={chooserFrom(active)}>
                    <Button>
                      <Plus className="h-4 w-4" aria-hidden /> {tr('ipd', 'new_order')}
                    </Button>
                  </Link>
                )}
              </>
            }
          />
        }
        nav={<ClinicalNav groups={groups} active={active} onSelect={open} labels={{ menu: tr('ipd', 'ws_emr_menu'), done: tr('ipd', 'ws_marker_done'), required: tr('ipd', 'ws_marker_required') }} />}
      >
        {isIpdClinicalForm(active) ? (
          <ClinicalFormSection
            key={active}
            scope={{ admissionId: admission.id }}
            code={active}
            closed={!current}
            canWrite={mayRecordAdmissionForm(active, { can, userId: user?.id ?? null }, careTeam)}
          />
        ) : (
          body[active]()
        )}
      </ClinicalShell>
      {recordingVitals && (
        <VitalsDialog
          open
          onClose={() => setRecordingVitals(false)}
          patientId={admission.patient.id}
          patientName={`${admission.patient.fullName} · ${admission.patient.mrn}`}
          admissionId={admission.id}
          invalidateKeys={[[...admissionKey(admission.id)]]}
          newborn={newbornFactsOf(admission.patient)}
        />
      )}
    </>
  );
}

function emrNavGroups(admission: Admission, chart: readonly IpdEmrChartSection[], entries: readonly IpdEmrFormEntry[]): ClinicalNavGroup[] {
  const counts: Partial<Record<IpdEmrSectionId, number>> = {
    vitals: admission.vitals.length,
    emar: admission.openWork?.dueDoses ?? 0,
    'nursing-notes': admission.nursingNotes.length,
    'progress-notes': admission.progressNotes.length,
    investigations: (admission.openWork?.labItems ?? 0) + (admission.openWork?.radiology ?? 0),
  };
  const item = (id: IpdEmrSectionId, required = false): ClinicalNavItem => ({
    id,
    label: sectionLabel(id),
    icon: sectionIcon(id),
    count: counts[id],
    required: required && admissionIsOpen(admission),
  });
  return [
    { id: 'chart', label: tr('ipd', 'ws_emr_group_chart'), items: chart.map((id) => item(id)) },
    { id: 'forms', label: tr('ipd', 'ws_emr_group_forms'), items: entries.map((entry) => item(entry.id, entry.required)) },
  ];
}
